import type { FieldMap, ViewBinding, ViewType } from './contract.js';

/**
 * LES GARDE-FOUS DU BINDING.
 *
 * Deux services, volontairement separes :
 *
 * 1. `unmetRequirement` tourne TOUJOURS : c'est lui qui grise une entree du
 *    selecteur AVEC SA RAISON. Une vue impossible n'est jamais absente, sinon
 *    "pourquoi mon kanban n'est pas propose" devient une classe de bug
 *    silencieuse (risque 4 de la specification).
 *
 * 2. `assertBinding` ne tourne qu'en DEVELOPPEMENT : il crie dans la console
 *    ce qu'aucune interface ne peut montrer (binding orphelin, catalogue
 *    manquant cote role OU cote colonne editable, doublon). En production, la
 *    branche disparait au build.
 */

/** Roles catalogues : sans `options()`, ils fabriquent des colonnes fantomes. */
const CATALOG_ROLES = new Set(['status', 'groupKey', 'lane']);

/** Pseudo-role reserve : "au moins une colonne declaree" (le tableau). */
const COLUMNS_REQUIREMENT = 'columns';

/** Les roles d'un binding, lus sans le mensonge de type de l'intersection. */
function rolesOf<T>(binding: ViewBinding<T>): Record<string, FieldMap<T> | undefined> {
  return binding.roles as Record<string, FieldMap<T> | undefined>;
}

/**
 * La raison pour laquelle cette vue n'est PAS proposable avec ce binding,
 * ou `null` si tout est satisfait. Le texte est montre tel quel au survol de
 * l'entree grisee : il dit ce qu'il manque ET ce qu'il faut declarer.
 */
export function unmetRequirement<T>(
  type: ViewType,
  binding: ViewBinding<T> | undefined,
): string | null {
  if (binding == null) {
    return `Aucun binding déclaré pour ${type.id} : ajoute-le à bindings dans useScreen.`;
  }

  const roles = rolesOf(binding);

  for (const requirement of type.requires ?? []) {
    if (requirement === COLUMNS_REQUIREMENT) {
      if ((binding.columns ?? []).length === 0) {
        return `Aucune colonne déclarée : ajoute columns au binding de ${type.id}.`;
      }
      continue;
    }

    const field = roles[requirement];
    if (field == null) {
      return `Rôle manquant : ${requirement}. Déclare-le dans roles du binding de ${type.id}.`;
    }
    if (CATALOG_ROLES.has(requirement) && typeof field.options !== 'function') {
      return `Le rôle ${requirement} doit fournir options() (le catalogue du tenant).`;
    }
  }

  return null;
}

/** Un probleme de declaration, tel qu'il est crie en developpement. */
export interface BindingProblem {
  /** La vue concernee, ou `null` pour un probleme de binding orphelin. */
  viewId: string | null;
  /** Le message, en francais, actionnable. */
  message: string;
}

/**
 * Passe en revue les types de vues et les bindings d'un ecran, et rend la
 * liste des problemes. Fonction PURE : c'est `assertBinding` qui parle.
 */
export function checkBindings<T>(
  types: readonly ViewType[],
  bindings: readonly ViewBinding<T>[],
): BindingProblem[] {
  const problems: BindingProblem[] = [];

  const seenTypes = new Set<string>();
  for (const type of types) {
    if (seenTypes.has(type.id)) {
      problems.push({ viewId: type.id, message: `Type de vue déclaré deux fois dans types.` });
    }
    seenTypes.add(type.id);
  }

  const seenBindings = new Set<string>();
  for (const binding of bindings) {
    if (seenBindings.has(binding.type)) {
      problems.push({ viewId: binding.type, message: `Binding déclaré deux fois pour ce type.` });
    }
    seenBindings.add(binding.type);

    if (!seenTypes.has(binding.type)) {
      problems.push({
        viewId: binding.type,
        message: `Binding orphelin : ce type n'est pas dans types, la vue ne sera jamais montée.`,
      });
    }

    // Un role catalogue sans options() : la seule facon d'eviter la colonne
    // fantome est de le dire au developpeur, tot et fort.
    for (const [name, field] of Object.entries(rolesOf(binding))) {
      if (field == null) continue;
      if (CATALOG_ROLES.has(name) && typeof field.options !== 'function') {
        problems.push({
          viewId: binding.type,
          message: `Le rôle ${name} n'expose pas options() : le catalogue du tenant est obligatoire.`,
        });
      }
    }

    // Meme regle pour les COLONNES editables : un editeur `select` sans
    // catalogue n'a aucune valeur a proposer, et le champ s'ouvrirait sur une
    // liste vide. Ces problemes ne passent PAS par `unmetRequirement` : une
    // colonne mal declaree ne doit pas griser la vue entiere, elle doit se
    // crier en console.
    for (const column of binding.columns ?? []) {
      if (column.kind === 'select' && typeof column.options !== 'function') {
        problems.push({
          viewId: binding.type,
          message: `La colonne ${column.key} est un select sans options() : l'éditeur n'aurait aucune valeur à proposer.`,
        });
      }
    }
  }

  for (const type of types) {
    const binding = bindings.find((candidate) => candidate.type === type.id);
    const reason = unmetRequirement(type, binding);
    if (reason != null) {
      problems.push({ viewId: type.id, message: `Entrée grisée dans le sélecteur : ${reason}` });
    }
  }

  return problems;
}

/**
 * Verification de DEVELOPPEMENT : ecrit les problemes dans la console.
 * En production, `import.meta.env.DEV` vaut false et tout le corps disparait
 * au build. Aucune exception n'est levee : un ecran mal declare doit rester
 * utilisable, juste bruyant.
 */
export function assertBinding<T>(
  screenId: string,
  types: readonly ViewType[],
  bindings: readonly ViewBinding<T>[],
): void {
  if (!import.meta.env.DEV) return;
  const problems = checkBindings(types, bindings);
  for (const problem of problems) {
    console.warn(`[taki/views] ${screenId}${problem.viewId ? ` / ${problem.viewId}` : ''} : ${problem.message}`);
  }
}
